import { useState } from 'react';

import {
  $insertNodes,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

import { Box } from '../../../../Box';
import { Icon } from '../../../../Icon';
import { DropdownButton } from '../../../../DropdownButton';
import { MenuItem } from '../../../../MenuItem';
import { Typography } from '../../../../Typography';
import { FileInputValue } from '../../../../input';
import { WysiwygConfigMode } from '../../WysiwygInput';
import { ToolbarButton } from '../common';
import {
  $createFileNode,
  $createImagesGalleryNode,
  $createSingleImageNode,
  $createVideoNode,
} from '../../nodes';

import { AddImageDialog } from './AddImageDialog';
import { AddImagesGalleryDialog } from './AddImagesGalleryDialog';
import { AddVideoDialog } from './AddVideoDialog';
import { AddFileDialog } from './AddFileDialog';

interface MediaPluginProps {
  loadFile: (
    file: File,
    type: 'file' | 'image',
  ) => Promise<{
    name: string;
    id: string;
    type: 'image' | 'file';
  }>;
  onUploadError: (message: string, title?: string) => void;
  singeImage: boolean;
  imagesGallery: boolean;
  video: boolean;
  file: boolean;
  mode: WysiwygConfigMode;
}

export const MediaPlugin = ({
  loadFile,
  onUploadError,
  singeImage,
  imagesGallery,
  video,
  file: filePlugin,
  mode,
}: MediaPluginProps) => {
  const [editor] = useLexicalComposerContext();

  const [isAddImageDialogOpened, setIsAddImageDialogOpened] = useState(false);
  const [isAddVideoDialogOpened, setIsAddVideoDialogOpened] = useState(false);
  const [isAddImageGalleryDialogOpened, setIsAddImageGalleryDialogOpened] =
    useState(false);
  const [isAddFileDialogOpened, setIsAddFileDialogOpened] = useState(false);

  const hasOneOfImagesNodes =
    (singeImage || imagesGallery) && !(singeImage && imagesGallery);

  const handleAddImageDialogState = (state: boolean) => () =>
    setIsAddImageDialogOpened(state);

  const handleAddImageGalleryDialogState = (state: boolean) => () =>
    setIsAddImageGalleryDialogOpened(state);

  const handleAddVideoDialogState = (state: boolean) => () =>
    setIsAddVideoDialogOpened(state);

  const handleAddFileDialogState = (state: boolean) => () =>
    setIsAddFileDialogOpened(state);

  const handleAddImage = async (
    file: FileInputValue,
    description: string | undefined,
  ) => {
    editor.read(async () => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        try {
          const { id, name } = await loadFile(file.value as File, 'image');

          editor.update(() => {
            const singleImageNode = $createSingleImageNode(
              name,
              id,
              description,
              mode,
            );
            $insertNodes([singleImageNode]);
          });
        } catch (e) {
          onUploadError(
            'Попробуйте еще раз позже',
            'Не удалось добавить изображение',
          );
        }
      }
    });
  };

  const handleAddImagesGallery = async (
    files: FileInputValue[],
    description: string | undefined,
  ) => {
    editor.read(async () => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        try {
          const images = await Promise.all(
            files.map((image) => loadFile(image.value as File, 'image')),
          );

          editor.update(() => {
            const imagesGalleryNode = $createImagesGalleryNode(
              images,
              editor,
              description,
            );
            $insertNodes([imagesGalleryNode]);
          });
        } catch (e) {
          onUploadError(
            'Попробуйте еще раз позже',
            'Не удалось добавить галерею изображений',
          );
        }
      }
    });
  };

  const handleAddVideo = (src: string, description: string) => {
    editor.read(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        try {
          editor.update(() => {
            const videoNode = $createVideoNode(src, description);
            const paragraphNode = $createParagraphNode();

            $insertNodes([videoNode, paragraphNode]);
          });
        } catch (e) {
          onUploadError(
            'Попробуйте еще раз позже',
            'Не удалось добавить видео',
          );
        }
      }
    });
  };

  const handleAddFile = async (file: FileInputValue) => {
    editor.read(async () => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        try {
          const { id, name } = await loadFile(file.value as File, 'file');

          editor.update(() => {
            const fileNode = $createFileNode(
              id,
              name,
              (file.value as File).size,
            );
            $insertNodes([fileNode]);
          });
        } catch (e) {
          onUploadError(
            'Попробуйте еще раз позже',
            'Не удалось прикрепить файл',
          );
        }
      }
    });
  };

  const imageMenuItems = [
    singeImage &&
      (hasOneOfImagesNodes ? (
        <ToolbarButton
          onClick={handleAddImageDialogState(true)}
          tooltip="Вставить изображение"
        >
          <Box display="flex" alignItems="center">
            <Icon name="picture_outline" size="16" color="text.light.primary" />
          </Box>
        </ToolbarButton>
      ) : (
        <MenuItem key="Изображение" onClick={handleAddImageDialogState(true)}>
          <Box display="flex" alignItems="center" gap="8">
            <Icon name="picture_outline" size="20" color="text.light.primary" />
            <Typography variant="body3">Изображение</Typography>
          </Box>
        </MenuItem>
      )),
    imagesGallery &&
      (hasOneOfImagesNodes ? (
        <ToolbarButton
          onClick={handleAddImageGalleryDialogState(true)}
          tooltip="Вставить изображение"
        >
          <Box display="flex" alignItems="center">
            <Icon
              name="picture_stack_outline"
              size="16"
              color="text.light.primary"
            />
          </Box>
        </ToolbarButton>
      ) : (
        <MenuItem
          key="Галерея изображений"
          onClick={handleAddImageGalleryDialogState(true)}
        >
          <Box display="flex" alignItems="center" gap="8">
            <Icon
              name="picture_stack_outline"
              size="20"
              color="text.light.primary"
            />
            <Typography variant="body3">Галерея изображений</Typography>
          </Box>
        </MenuItem>
      )),
  ];

  return (
    <Box display="flex" alignItems="center" gap="2">
      {(singeImage || imagesGallery) &&
        (hasOneOfImagesNodes ? (
          imageMenuItems
        ) : (
          <DropdownButton
            menuItems={imageMenuItems}
            variant="tertiaryLight"
            verticalPosition="bottom"
            horizontalPosition="left"
            button={({ onClick }) => (
              <ToolbarButton onClick={onClick} tooltip="Вставить изображение">
                <Box display="flex" alignItems="center" gap="2">
                  <Icon
                    name="picture_outline"
                    size="16"
                    color="text.light.primary"
                  />
                  <Icon name="dropdown" size="16" color="text.light.primary" />
                </Box>
              </ToolbarButton>
            )}
          />
        ))}

      {video && (
        <ToolbarButton
          onClick={handleAddVideoDialogState(true)}
          tooltip="Вставить ссылку на видео"
        >
          <Icon
            name="video_square_outline"
            size="16"
            color="text.light.primary"
          />
        </ToolbarButton>
      )}
      {filePlugin && (
        <ToolbarButton
          onClick={handleAddFileDialogState(true)}
          tooltip="Вставить файл"
        >
          <Icon name="attach" size="16" color="text.light.primary" />
        </ToolbarButton>
      )}
      {isAddImageDialogOpened && (
        <AddImageDialog
          open={isAddImageDialogOpened}
          onClose={handleAddImageDialogState(false)}
          addImage={handleAddImage}
        />
      )}
      {isAddImageGalleryDialogOpened && (
        <AddImagesGalleryDialog
          open={isAddImageGalleryDialogOpened}
          onClose={handleAddImageGalleryDialogState(false)}
          addImages={handleAddImagesGallery}
        />
      )}
      {isAddVideoDialogOpened && (
        <AddVideoDialog
          open={isAddVideoDialogOpened}
          onClose={handleAddVideoDialogState(false)}
          addVideo={handleAddVideo}
        />
      )}
      {isAddFileDialogOpened && (
        <AddFileDialog
          open={isAddFileDialogOpened}
          onClose={handleAddFileDialogState(false)}
          addFile={handleAddFile}
        />
      )}
    </Box>
  );
};
